"use client";

import { useEffect, useRef } from "react";
import { AnalyticsEvents, trackEvent } from "@/lib/client/analytics";

const DEDUPE_MS = 30 * 60 * 1000;
const STORAGE_PREFIX = "tutuly:blog-view:";

function storageKey(blogId: string): string {
  return `${STORAGE_PREFIX}${blogId}`;
}

function wasViewedRecently(blogId: string): boolean {
  try {
    const raw = localStorage.getItem(storageKey(blogId));
    if (!raw) return false;
    const ts = Number(raw);
    return Number.isFinite(ts) && Date.now() - ts < DEDUPE_MS;
  } catch {
    return false;
  }
}

function markViewed(blogId: string): void {
  try {
    localStorage.setItem(storageKey(blogId), String(Date.now()));
  } catch {
    // ignore quota / private mode
  }
}

function isLikelyAutomationClient(): boolean {
  if (typeof navigator === "undefined") return true;
  if (navigator.webdriver) return true;
  return false;
}

interface BlogViewTrackerProps {
  id: string;
  slug?: string | null;
  title: string;
}

/**
 * Records a storefront blog view once per browser (30m) and fires analytics.
 * Server cookie dedupe is the source of truth for DB increments.
 */
export function BlogViewTracker({ id, slug, title }: BlogViewTrackerProps) {
  const trackedRef = useRef(false);

  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;

    trackEvent(AnalyticsEvents.BLOG_POST_VIEWED, {
      blog_id: id,
      blog_slug: slug ?? undefined,
      blog_title: title,
    });

    if (isLikelyAutomationClient() || wasViewedRecently(id)) {
      return;
    }

    markViewed(id);

    void fetch("/api/blog/view", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, slug: slug ?? undefined }),
      keepalive: true,
    }).catch(() => {
      // Non-blocking: ranking can lag if the request fails
    });
  }, [id, slug, title]);

  return null;
}
